import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Send, Loader2, MessageCircle, Phone, ArrowLeft } from 'lucide-react';
import CustomerBottomNav from '@/components/CustomerBottomNav';
import CustomerSidebar from '@/components/CustomerSidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerChat = () => {
  const navigate = useNavigate();
  const [messages, setMessages]     = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading]       = useState(true);
  const [sending, setSending]       = useState(false);

  const messagesEndRef = useRef(null);
  const prevMsgCount   = useRef(0);
  const audioRef       = useRef(null);
  const isFirstLoad    = useRef(true);
  const inputRef       = useRef(null);

  const token = localStorage.getItem('customerToken');

  useEffect(() => {
    audioRef.current = new Audio('/notification.mp3');
    audioRef.current.volume = 0.6;
  }, []);

  useEffect(() => {
    fetchMessages();
    const iv = setInterval(fetchMessages, 5000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const playSound = () => {
    try { audioRef.current?.play().catch(() => {}); } catch (_) {}
  };

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/customer/chat`, { headers: { Authorization: `Bearer ${token}` } });
      const msgs = res.data;
      if (!isFirstLoad.current && msgs.length > prevMsgCount.current) {
        const latest = msgs[msgs.length - 1];
        if (latest?.sender_type === 'admin') {
          playSound();
          toast.info('New reply from JustFold support');
        }
      }
      isFirstLoad.current = false;
      prevMsgCount.current = msgs.length;
      setMessages(msgs);
    } catch (_) {}
    finally { setLoading(false); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/customer/chat`, { message: newMessage.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setNewMessage('');
      fetchMessages();
      inputRef.current?.focus();
    } catch { toast.error('Failed to send message'); }
    finally { setSending(false); }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((acc, msg) => {
    const dateKey = format(new Date(msg.created_at), 'dd MMM yyyy');
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(msg);
    return acc;
  }, {});

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      <CustomerSidebar />

      <div className="flex-1 flex flex-col overflow-hidden">

        {/* ── Chat header ── */}
        <header className="bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3 flex-shrink-0 z-10">
          {/* Mobile back */}
          <button onClick={() => navigate('/customer/dashboard')}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>

          {/* Avatar */}
          <div className="relative">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-200 flex-shrink-0">
              <img src="/justfold-logo-transparent.png" alt="" className="w-6 h-6" style={{ filter: 'brightness(0) invert(1)' }} />
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-black text-slate-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>JustFold Support</p>
            <p className="text-xs text-green-600 font-medium">● Online</p>
          </div>

          <a href="tel:+919821381888"
            className="flex items-center gap-1.5 bg-green-50 hover:bg-green-100 border border-green-200 text-green-700 text-xs font-semibold px-3 py-2 rounded-xl transition-all">
            <Phone className="w-3.5 h-3.5" /> Call
          </a>
        </header>

        {/* ── Messages area ── */}
        <main className="flex-1 overflow-y-auto px-4 md:px-8 py-4">
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center">
                <MessageCircle className="w-8 h-8 text-blue-400" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-bold text-slate-600 mb-1">No messages yet</p>
                <p className="text-sm text-slate-400">Send us a message and we'll reply shortly</p>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-2xl px-4 py-3 max-w-xs text-center">
                <p className="text-xs text-blue-600 font-medium">💡 You can also call us at +91 98213 81888</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 max-w-2xl mx-auto">
              {Object.entries(groupedMessages).map(([date, msgs]) => (
                <div key={date}>
                  {/* Date separator */}
                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-100 px-3 py-1 rounded-full">{date}</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>

                  <div className="space-y-2">
                    {msgs.map((msg) => {
                      const isCustomer = msg.sender_type === 'customer';
                      return (
                        <div key={msg.id} className={`flex ${isCustomer ? 'justify-end' : 'justify-start'}`}>
                          {!isCustomer && (
                            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 mr-2 mt-1 self-end shadow-sm">
                              <img src="/justfold-logo-transparent.png" alt="" className="w-4 h-4" style={{ filter: 'brightness(0) invert(1)' }} />
                            </div>
                          )}
                          <div className={`max-w-[75%] ${isCustomer ? '' : ''}`}>
                            {!isCustomer && (
                              <p className="text-[10px] font-bold text-blue-600 mb-1 ml-1">JustFold Support</p>
                            )}
                            <div className={`px-4 py-2.5 rounded-2xl text-sm ${
                              isCustomer
                                ? 'bg-blue-600 text-white rounded-br-md shadow-md shadow-blue-100'
                                : 'bg-white text-slate-800 rounded-bl-md shadow-sm border border-slate-100'
                            }`}>
                              <p className="leading-relaxed">{msg.message}</p>
                              <p className={`text-[10px] mt-1.5 ${isCustomer ? 'text-blue-200 text-right' : 'text-slate-400'}`}>
                                {format(new Date(msg.created_at), 'hh:mm a')}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </main>

        {/* ── Message input ── */}
        <div className="bg-white border-t border-slate-200 flex-shrink-0 pb-16 md:pb-0">
          <form onSubmit={sendMessage} className="max-w-2xl mx-auto px-4 md:px-8 py-3 flex items-center gap-2">
            <input
              ref={inputRef}
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              disabled={sending}
              data-testid="chat-input"
              className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 rounded-2xl text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors"
            />
            <button
              type="submit"
              disabled={sending || !newMessage.trim()}
              data-testid="chat-send"
              className="w-11 h-11 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-2xl flex items-center justify-center transition-all shadow-md shadow-blue-200 hover:shadow-lg hover:shadow-blue-200 flex-shrink-0"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>

      </div>

      <CustomerBottomNav />
    </div>
  );
};

export default CustomerChat;
